import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Copy as CopyIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Loader as LoaderIcon,
  MoreHorizontal as MoreHorizontalIcon,
  Pencil as PencilIcon,
  Trash2 as TrashIcon,
} from "lucide-react";
import type { WhatsAppTemplateDto, WhatsAppTemplatePresentation } from "@multi-chat/shared";
import {
  WHATSAPP_IMAGE_MAX_BYTES,
  isWhatsAppImageMimeAllowed,
} from "@multi-chat/shared";
import { Badge } from "../../../components/ui/badge/index.js";
import { Button } from "../../../components/ui/button/index.js";
import { ImageDropzone } from "../../../components/common/ImageDropzone.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { TechnicalValue } from "../TechnicalValue.js";
import { WhatsAppTemplatePreview } from "../WhatsAppTemplatePreview/index.js";
import { canDuplicateTemplate } from "../template-management/template-editor-support.js";
import { findLanguageLabel } from "../template-management/template-language-select-state.js";
import type { TemplateLanguageOption } from "../template-management/template-language-options.js";
import { useWhatsAppTemplateHeaderImageUrl } from "../use-whatsapp-template-header-image.js";
import {
  buildTemplateListDetailsModel,
  formatTemplateListCategoryToken,
  getTemplateListOverflowAvailability,
  splitLanguageOptionLabel,
  templateListCategoryI18nKey,
  templateListStatusBadgeVariant,
  templateListStatusLabelKey,
} from "../template-list-presentation.js";

/** Column count shared by the header row, detail rows, and skeleton. */
export const TEMPLATE_TABLE_COLUMN_COUNT = 5;

const spinClass = "animate-[spin_0.8s_linear_infinite]";
const cellClass =
  "border-e border-b border-e-line/60 border-b-line px-4 py-3 align-middle text-start last:border-e-0 group-last/row:border-b-0";
const centeredCellClass = "text-center";
const rowEase = [0.22, 1, 0.36, 1] as const;

function categoryBadgeClass(category: string): string {
  switch (category.trim().toUpperCase()) {
    case "MARKETING":
      return "bg-accent-soft text-accent ring-accent/20";
    case "UTILITY":
      return "bg-surface-muted text-ink ring-line-strong";
    case "AUTHENTICATION":
      return "bg-warning-muted text-warning ring-warning/25";
    default:
      return "bg-surface-muted text-ink-muted ring-line";
  }
}

function formatSyncedAt(iso: string, locale: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

function TemplateMissingHeaderImageAttach({
  onAttach,
}: {
  onAttach: (file: File) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onSelectFile(next: File) {
    setError(null);
    if (!isWhatsAppImageMimeAllowed(next.type)) {
      setError(t("whatsapp.templates.manage.wizard.editor.imageInvalidType"));
      return;
    }
    if (next.size > WHATSAPP_IMAGE_MAX_BYTES) {
      setError(t("whatsapp.templates.manage.wizard.editor.imageTooLarge"));
      return;
    }
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setFile(next);
    setPreviewUrl(URL.createObjectURL(next));
  }

  return (
    <div className="grid gap-2">
      <p className="m-0 text-[0.75rem] text-ink-muted">
        {t("inbox.templateSend.errors.headerImageMissing")}
      </p>
      <ImageDropzone
        previewUrl={previewUrl}
        fileName={file?.name ?? null}
        byteSize={file?.size ?? null}
        accept="image/jpeg,image/png"
        busy={busy}
        error={error}
        selectLabel={t("whatsapp.templates.manage.wizard.editor.imageSelect")}
        changeLabel={t("whatsapp.templates.manage.wizard.editor.imageChange")}
        removeLabel={t("whatsapp.templates.manage.wizard.editor.imageRemove")}
        dropLabel={t("whatsapp.templates.manage.wizard.editor.imageDrop")}
        emptyHint={t("whatsapp.templates.manage.wizard.editor.imageHint")}
        onSelectFile={onSelectFile}
        onRemove={() => {
          if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
          setFile(null);
          setPreviewUrl(null);
        }}
      />
      <Button
        type="button"
        size="sm"
        disabled={!file || busy}
        onClick={() => {
          if (!file) return;
          setBusy(true);
          void onAttach(file)
            .catch(() => {
              setError(t("whatsapp.templates.manage.wizard.editor.imageRequired"));
            })
            .finally(() => setBusy(false));
        }}
      >
        {t("whatsapp.templates.manage.wizard.editor.imageSelect")}
      </Button>
    </div>
  );
}

export interface TemplateListRowProps {
  organizationId: number;
  template: WhatsAppTemplateDto;
  canManage: boolean;
  languageOptions: readonly TemplateLanguageOption[];
  pendingDelete: boolean;
  deleting: boolean;
  deleteError: string | null;
  onEdit: () => void;
  onDuplicate: () => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onAttachHeaderImage?: (templateId: number, file: File) => Promise<void>;
  previewOpen: boolean;
  onTogglePreview: () => void;
}

function categoryLabel(
  category: string,
  translate: (key: string) => string,
): string {
  const key = templateListCategoryI18nKey(category);
  const translated = translate(key);
  return translated === key ? formatTemplateListCategoryToken(category) : translated;
}

function statusLabel(
  status: string,
  translate: (key: string) => string,
): string {
  const key = templateListStatusLabelKey(status);
  const translated = translate(key);
  return translated === key ? status : translated;
}


function RowIconAction({
  label,
  onClick,
  children,
  destructive = false,
  pressed,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  destructive?: boolean;
  pressed?: boolean;
}): React.JSX.Element {
  return (
    <SimpleTooltip content={label}>
      <button
        type="button"
        className={cn(
          "flex size-7 items-center justify-center rounded-md text-ink-muted transition-[background-color,color,transform] duration-150 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent motion-reduce:hover:translate-y-0",
          destructive
            ? "hover:bg-error-muted hover:text-error"
            : "hover:bg-surface-muted hover:text-ink",
          pressed && (destructive ? "bg-error-muted text-error" : "bg-accent-soft text-accent"),
        )}
        aria-label={label}
        aria-pressed={pressed}
        onClick={onClick}
      >
        {children}
      </button>
    </SimpleTooltip>
  );
}

function PreviewFact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid min-w-0 gap-1">
      <dt className="m-0 text-[0.6875rem] font-medium text-ink-subtle">{label}</dt>
      <dd className="m-0 min-w-0 text-[0.8125rem] font-medium text-ink">{children}</dd>
    </div>
  );
}

/** Animated full-width row below a template (preview or delete confirm). */
function ExpandRow({
  children,
  className,
  reduceMotion,
  onOpened,
}: {
  children: React.ReactNode;
  className?: string;
  reduceMotion: boolean;
  onOpened?: (element: HTMLDivElement) => void;
}): React.JSX.Element {
  const innerRef = useRef<HTMLDivElement>(null);
  const duration = reduceMotion ? 0 : 0.24;
  return (
    <tr className={className}>
      <td colSpan={TEMPLATE_TABLE_COLUMN_COUNT} className="border-b border-line p-0">
        <motion.div
          ref={innerRef}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration, ease: rowEase }}
          className="overflow-hidden"
          onAnimationComplete={(definition) => {
            if (definition === "exit" || !innerRef.current) return;
            onOpened?.(innerRef.current);
          }}
        >
          {children}
        </motion.div>
      </td>
    </tr>
  );
}

export function TemplateListRow({
  organizationId,
  template,
  canManage,
  languageOptions,
  pendingDelete,
  deleting,
  deleteError,
  onEdit,
  onDuplicate,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onAttachHeaderImage,
  previewOpen,
  onTogglePreview,
}: TemplateListRowProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const overflow = getTemplateListOverflowAvailability(canManage, template.status);
  const canDuplicate = canManage && canDuplicateTemplate(template);
  const language = findLanguageLabel(languageOptions, template.language);
  const languageParts = splitLanguageOptionLabel(language, template.language);
  const category = categoryLabel(template.category, t);
  const syncedLabel = formatSyncedAt(template.syncedAt, i18n.language);
  const details = previewOpen ? buildTemplateListDetailsModel(template) : null;
  const skipMenuRestoreFocusRef = useRef(false);
  const headerImageUrl = useWhatsAppTemplateHeaderImageUrl(
    organizationId,
    template.id,
    previewOpen && template.headerImageMissing !== true && template.headerMedia != null,
  );
  const preview: WhatsAppTemplatePresentation | null = details
    ? details.preview.header?.type === "IMAGE"
      ? {
          ...details.preview,
          header: {
            type: "IMAGE",
            ...(headerImageUrl ? { imageUrl: headerImageUrl } : {}),
            missing: !headerImageUrl,
          },
        }
      : details.preview
    : null;
  const previewLabel = previewOpen
    ? t("whatsapp.templates.list.hidePreviewAction")
    : t("whatsapp.templates.list.previewAction");
  const keepInView = (element: HTMLDivElement): void => {
    element.scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
  };

  return (
    <>
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.18 }}
        className={cn(
          "group/row transition-colors duration-150",
          previewOpen ? "bg-surface-selected" : "hover:bg-surface-hover/60",
        )}
      >
        <td className={cn(cellClass, centeredCellClass, "max-w-[18rem]")}>
          <span
            className="mx-auto block max-w-full truncate text-[0.875rem] font-semibold text-ink"
            title={template.name}
          >
            <TechnicalValue>{template.name}</TechnicalValue>
          </span>
        </td>
        <td className={cn(cellClass, centeredCellClass, "hidden md:table-cell")}>
          <TechnicalValue className="font-mono text-[0.75rem] text-ink-muted">
            {template.providerTemplateId}
          </TechnicalValue>
        </td>
        <td className={cn(cellClass, centeredCellClass)}>
          <Badge variant={templateListStatusBadgeVariant(template.status)}>
            {statusLabel(template.status, t)}
          </Badge>
        </td>
        <td className={cn(cellClass, centeredCellClass, "hidden sm:table-cell")}>
          <span
            className={cn(
              "inline-flex items-center rounded-md px-2 py-0.5 text-[0.6875rem] font-semibold tracking-[0.04em] ring-1",
              categoryBadgeClass(template.category),
            )}
            title={category}
          >
            <TechnicalValue>{template.category.trim().toUpperCase()}</TechnicalValue>
          </span>
        </td>
        <td className={cn(cellClass, "w-px whitespace-nowrap")}>
          <div className="flex items-center justify-center">
            <div className="hidden items-center gap-1 rounded-lg border border-line bg-surface p-1 shadow-sm transition-shadow duration-150 group-hover/row:shadow-md sm:flex">
              <RowIconAction label={previewLabel} onClick={onTogglePreview} pressed={previewOpen}>
                {previewOpen ? (
                  <EyeOffIcon size={15} aria-hidden="true" />
                ) : (
                  <EyeIcon size={15} aria-hidden="true" />
                )}
              </RowIconAction>
              {canDuplicate ? (
                <RowIconAction
                  label={t("whatsapp.templates.list.duplicateAction")}
                  onClick={onDuplicate}
                >
                  <CopyIcon size={15} aria-hidden="true" />
                </RowIconAction>
              ) : null}
              {overflow.edit ? (
                <RowIconAction label={t("whatsapp.templates.manage.editAction")} onClick={onEdit}>
                  <PencilIcon size={15} aria-hidden="true" />
                </RowIconAction>
              ) : null}
              {overflow.delete ? (
                <RowIconAction
                  label={t("whatsapp.templates.manage.deleteAction")}
                  onClick={onRequestDelete}
                  destructive
                  pressed={pendingDelete}
                >
                  <TrashIcon size={15} aria-hidden="true" />
                </RowIconAction>
              ) : null}
            </div>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="size-8 px-0 sm:hidden"
                  aria-label={t("whatsapp.templates.list.moreActions")}
                >
                  <MoreHorizontalIcon size={16} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                onCloseAutoFocus={(event) => {
                  if (!skipMenuRestoreFocusRef.current) return;
                  event.preventDefault();
                  skipMenuRestoreFocusRef.current = false;
                }}
              >
                <DropdownMenuItem onSelect={onTogglePreview}>
                  <EyeIcon size={16} aria-hidden="true" />
                  {previewLabel}
                </DropdownMenuItem>
                {canDuplicate ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      skipMenuRestoreFocusRef.current = true;
                      onDuplicate();
                    }}
                  >
                    <CopyIcon size={16} aria-hidden="true" />
                    {t("whatsapp.templates.list.duplicateAction")}
                  </DropdownMenuItem>
                ) : null}
                {overflow.edit ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      skipMenuRestoreFocusRef.current = true;
                      onEdit();
                    }}
                  >
                    <PencilIcon size={16} aria-hidden="true" />
                    {t("whatsapp.templates.manage.editAction")}
                  </DropdownMenuItem>
                ) : null}
                {overflow.delete ? (
                  <DropdownMenuItem className="text-error" onSelect={onRequestDelete}>
                    <TrashIcon size={16} aria-hidden="true" />
                    {t("whatsapp.templates.manage.deleteAction")}
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </td>
      </motion.tr>

      <AnimatePresence initial={false}>
        {pendingDelete ? (
          <ExpandRow key="delete" className="bg-error-muted/50" reduceMotion={reduceMotion}>
            <div
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              role="group"
              aria-label={t("whatsapp.templates.list.deleteConfirmTitle")}
            >
              <div className="flex min-w-0 items-start gap-2.5 text-start">
                <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-error-muted text-error">
                  <TrashIcon size={14} aria-hidden="true" />
                </span>
                <div className="grid min-w-0 gap-0.5">
                  <p className="m-0 text-sm font-semibold leading-snug text-ink">
                    {t("whatsapp.templates.list.deleteConfirmTitle")}
                  </p>
                  <p className="m-0 text-[0.75rem] leading-snug text-ink-muted">
                    {t("whatsapp.templates.list.deleteConfirmMessage")}
                  </p>
                  {deleteError ? (
                    <p className="m-0 text-[0.75rem] leading-snug text-error" role="alert">
                      {deleteError}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-md bg-surface px-3"
                  onClick={onCancelDelete}
                  disabled={deleting}
                >
                  {t("whatsapp.templates.manage.cancel")}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="h-8 rounded-md px-3"
                  onClick={onConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <LoaderIcon size={14} className={spinClass} aria-hidden="true" />
                  ) : null}
                  {deleting
                    ? t("whatsapp.templates.manage.deleting")
                    : t("whatsapp.templates.manage.deleteAction")}
                </Button>
              </div>
            </div>
          </ExpandRow>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {previewOpen && details ? (
          <ExpandRow
            key="preview"
            className="bg-surface-muted/50"
            reduceMotion={reduceMotion}
            onOpened={keepInView}
          >
            <div className="grid gap-4 px-4 py-4 md:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] md:items-start md:gap-5">
              <div className="grid gap-3">
                <div className="rounded-xl border border-line bg-[color-mix(in_srgb,var(--color-surface-muted)_85%,var(--color-accent)_15%)] p-4">
                  <WhatsAppTemplatePreview
                    presentation={preview ?? details.preview}
                    contentLanguage={template.language}
                    imagePlaceholderLabel={t("whatsapp.templates.manage.wizard.editor.headerImage")}
                  />
                </div>
                {template.headerImageMissing === true && onAttachHeaderImage ? (
                  <TemplateMissingHeaderImageAttach
                    onAttach={(file) => onAttachHeaderImage(template.id, file)}
                  />
                ) : null}
              </div>
              <div className="grid content-start gap-3 text-start">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate text-sm font-semibold text-ink">
                    <TechnicalValue>{template.name}</TechnicalValue>
                  </span>
                  <Badge variant={templateListStatusBadgeVariant(template.status)}>
                    {statusLabel(template.status, t)}
                  </Badge>
                </div>
                <dl className="m-0 grid grid-cols-2 gap-x-5 gap-y-3 rounded-lg border border-line bg-surface p-3.5 shadow-sm">
                  <PreviewFact label={t("whatsapp.templates.list.columns.category")}>
                    {category}{" "}
                    <TechnicalValue className="text-[0.75rem] text-ink-subtle">
                      ({template.category.trim().toUpperCase()})
                    </TechnicalValue>
                  </PreviewFact>
                  <PreviewFact label={t("whatsapp.templates.manage.language")}>
                    {languageParts.localized}
                    {languageParts.localized !== details.languageLabel ? (
                      <span className="block text-[0.75rem] font-normal text-ink-subtle">
                        {details.languageLabel}
                      </span>
                    ) : null}
                  </PreviewFact>
                  <PreviewFact label={t(details.variableTypeFieldKey)}>
                    {t(details.variableTypeValueKey)}
                  </PreviewFact>
                  {syncedLabel ? (
                    <PreviewFact label={t("whatsapp.templates.list.columns.synced")}>
                      {syncedLabel}
                    </PreviewFact>
                  ) : null}
                  <div className="col-span-2">
                    <PreviewFact label={t("whatsapp.templates.list.detailPanel.metaId")}>
                      <TechnicalValue className="font-mono text-[0.75rem] wrap-anywhere">
                        {template.providerTemplateId}
                      </TechnicalValue>
                    </PreviewFact>
                  </div>
                </dl>
                {details.hasUnhandledComponents || details.editSurface.kind === "unsupported" ? (
                  <p className="m-0 rounded-md border border-line bg-surface px-3 py-2 text-[0.75rem] leading-snug text-ink-muted">
                    {t("whatsapp.templates.list.detailPanel.unsupportedNote")}
                  </p>
                ) : null}
              </div>
            </div>
          </ExpandRow>
        ) : null}
      </AnimatePresence>
    </>
  );
}

export function TemplateListSkeleton({ count = 6 }: { count?: number }): React.JSX.Element {
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="h-9 border-b border-line bg-surface-muted/70" />
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex items-center gap-6 border-b border-line px-4 py-3 last:border-b-0">
          <span className="grid flex-1 gap-1.5">
            <span className="h-3.5 w-40 max-w-full animate-pulse rounded-sm bg-surface-muted" />
            <span className="h-2.5 w-24 animate-pulse rounded-sm bg-surface-muted" />
          </span>
          <span className="hidden h-3 w-28 animate-pulse rounded-sm bg-surface-muted md:block" />
          <span className="h-5 w-16 animate-pulse rounded-full bg-surface-muted" />
          <span className="hidden h-5 w-20 animate-pulse rounded-md bg-surface-muted sm:block" />
          <span className="h-7 w-24 animate-pulse rounded-lg bg-surface-muted" />
        </div>
      ))}
    </div>
  );
}
